/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */
import React from 'react';
import ReactHtmlParser from 'react-html-parser';
import {
    object, func, number, shape, string,
} from 'prop-types';
import withStyles from '@material-ui/core/styles/withStyles';
import Select from '@material-ui/core/Select';
import Typography from '@material-ui/core/Typography';
import { useUIDSeed } from 'react-uid';
import MobileCYOItem from './MobileCYOItems';

const styles = ({ palette }) => ({
    cyoWrapper: {
        float: 'left',
        width: '120px',
        height: '250px',
        marginRight: '6px',
        textAlign: 'center',
        position: 'relative',
    },
    cyoImage: {
        width: '120px',
        height: '131px',
    },
    prodName: {
        height: '50px',
        overflow: 'hidden',
        fontSize: '13px',
        lineHeight: '1.3',
        textOverflow: 'ellipsis',
        display: '-webkit-box',
        '-webkit-line-clamp': '3',
        '-webkit-box-orient': 'vertical',
    },
    cyoSelectBox: {
        fontSize: '12px',
        padding: '3px 24px 3px 10px',
    },
    cyoSelect: {
        fontSize: '12px',
        border: `1px solid ${palette.black}`,
        borderRadius: '4px',
        '&:before': {
            content: 'none',
        },
        '&:after': {
            content: 'none',
        },
    },
    checkIcon: {
        display: 'block',
        position: 'absolute',
        top: '-5px',
        right: '-5px',
        width: '28px',
        height: '28px',
        background: palette?.cms?.['Dark Green'],
        color: palette.white,
        fontSize: '20px',
        lineHeight: '28px',
        textAlign: 'center',
        textIndent: '-3px',
        border: 'solid 2px #fff',
        borderRadius: '50%',
        zIndex: '2',
        boxShadow: '2px 2px 3px rgba(0, 0, 0, .2)',
        transition: 'opacity .3s',
    },
    errorMessage: {
        color: palette.common.red,
        fontSize: '14px',
        padding: '2px',
    },
});

// This function will return Out of stock text to be displayed below the CYO product
const getErrorMessage = (error, productId) => {
    if (!error) return '';

    // Handle the below error message from API response when - add to cart fails
    // FP001435: Product FP001435 is currently out of stock
    if (error.match(/currently out of stock/ig)) {
        const errorProductId = error.split(':')[0];
        const matchProduct = new RegExp(errorProductId, 'ig');
        if (productId.match(matchProduct)) {
            return 'Out of stock';
        }
    }

    return '';
};

const CYOItems = ({
    classes, component, handleSelectedCyoProduct, getOptionSelectList, maxComponents,
    cyoGroup, selectedItems, totalProductSelected, cartError, displayView,
}) => {
    const seed = useUIDSeed();
    if (Object.keys(component).length === 0) {
        return null;
    }
    const maxSelectables = maxComponents || cyoGroup.numberofUnitsRequiredForComponent;
    const qtySelected = parseInt(selectedItems?.[component.legacyId], 10) || 0;

    // regex will replace every alphabetic or numberic character before ":" with empty string ''
    // thus replacing http or https from image url
    const itemImagePath = component.image?.path?.replace(/(^\w+:|^)/, '');
    const itemImageName = component.image?.name;

    const errorMessage = getErrorMessage(cartError, component.id);
    const desktopCYOItems = () => (
        <div className={classes.cyoWrapper} key={seed(component)}>
            <img src={`${itemImagePath}${itemImageName}c.jpg`} alt={component.name} className={classes.cyoImage} />
            {qtySelected > 0 && (<svg className={classes.checkIcon} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path style={{ fill: '#ffff' }} d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z" /></svg>)}
            <Typography className={classes.prodName}>{ReactHtmlParser(component.name)}</Typography>
            <Select
                native
                className={classes.cyoSelect}
                classes={{
                    select: classes.cyoSelectBox,
                }}
                value={qtySelected}
                onChange={handleSelectedCyoProduct(component, maxSelectables, cyoGroup)}
                inputProps={{
                    name: `select_${component.legacyId}`,
                    id: `select_${component.legacyId}`,
                }}
            >
                {getOptionSelectList(maxSelectables, selectedItems, component, totalProductSelected)}
            </Select>
            {errorMessage && <div className={classes.errorMessage}>{errorMessage}</div>}
        </div>
    );

    const mobileCYOItems = () => (
        <>
            {qtySelected === 0
                && (
                    <MobileCYOItem
                        maxComponents={maxComponents}
                        cyoGroup={cyoGroup}
                        selectedItems={selectedItems}
                        component={component}
                        handleSelectedCyoProduct={handleSelectedCyoProduct}
                        errorMessage={errorMessage}
                    />
                )}
        </>
    );

    if (displayView === 'mobile') {
        return (mobileCYOItems());
    }
    return (desktopCYOItems());
};

CYOItems.propTypes = {
    classes: object.isRequired,
    cyoGroup: shape({
        numberofUnitsRequiredForComponent: number.isRequired,
    }).isRequired,
    component: shape({
        id: string.isRequired,
        legacyId: string.isRequired,
        name: string.isRequired,
        image: shape({
            name: string.isRequired,
            path: string.isRequired,
        }),
    }).isRequired,
    maxComponents: number.isRequired,
    handleSelectedCyoProduct: func.isRequired,
    getOptionSelectList: func.isRequired,
    selectedItems: object.isRequired,
    totalProductSelected: number.isRequired,
    cartError: string.isRequired,
    displayView: string,
};

CYOItems.defaultProps = {
    displayView: 'desktop',
};

export default withStyles(styles)(CYOItems);
